import { Link, useNavigate, useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { server } from "../server";
import { toast } from "react-toastify";
import GLightbox from "glightbox";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import "glightbox/dist/css/glightbox.css";
import imageCompression from "browser-image-compression";

function Shared() {
  const { event, pin, quality, imagetype } = useParams();
  const navigate = useNavigate();
  const [eventDetails, setEventDetails] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinDigits, setPinDigits] = useState(["", "", "", ""]);
  const token = localStorage.getItem("authToken");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [stream, setStream] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [loadedImages, setLoadedImages] = useState({});
  const canUpload = eventDetails?.category === "basic";
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(300);
  const [totalImages, setTotalImages] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);
  const [totalProgress, setTotalProgress] = useState(0);
  const [processingFiles, setProcessingFiles] = useState(false);
  const currentBatchRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const MAX_FILES = 500;
  const [limitError, setLimitError] = useState("");

  const handleShare = async () => {
    const shareUrl = window.location.href;
    const title = eventDetails?.name || "Check out this event!";
    const text = eventDetails?.description || "Look at these amazing photos!";

    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text,
          url: shareUrl,
        });
      } catch (err) {
        console.log("Share canceled or failed:", err);
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      toast.info("Link copied to clipboard!");
    }
  };

  const fetchImages = async (pageNum = page) => {
    try {
      setLoading(true);

      const response = await fetch(
        `${server}/Images/GetImagesByEventId?eventId=${event}&page=${pageNum}&limit=${limit}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        }
      );

      const data = await response.json();
      setEventDetails(data.event || null);
      if (data?.images) {
        setImages(data.images);
        setTotalImages(data.pagination.total);
        setTotalPages(data.pagination.totalPages);
      } else {
        setImages([]);
      }

      setPage(pageNum);
    } catch (error) {
      console.error(error);
      setImages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages(page);
  }, [page]);

  useEffect(() => {
    if (!pin || pin.trim() === "") {
      setShowPinModal(true);
      setLoading(false);
    } else {
      fetchImages();
    }
  }, [event, pin]);

  useEffect(() => {
    if (!pin || pin.trim() === "") {
      setShowPinModal(true);
    }
  }, [pin]);

  const handleChange = (e, index) => {
    const { value } = e.target;
    if (!/^[0-9]?$/.test(value)) return;

    const updated = [...pinDigits];
    updated[index] = value;
    setPinDigits(updated);

    if (value && index < pinDigits.length - 1) {
      document.getElementById(`pin-${index + 1}`).focus();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const enteredPin = pinDigits.join("");

    if (enteredPin !== eventDetails?.pin) {
      toast.error("Incorrect PIN");
      return;
    }

    setShowPinModal(false);
    navigate(`/shared/${event}/${enteredPin}/${quality}/${imagetype}`, {
      replace: true,
    });
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      toast.error("Camera Access Denied");
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || video.readyState < 2) {
      toast.info("Please Wait, Starting Camera", { autoClose: 2000 });
      return;
    }

    const ctx = canvas.getContext("2d");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const imageDataUrl = canvas.toDataURL("image/jpeg");
    setCapturedImage(imageDataUrl);

    canvas.toBlob(async (blob) => {
      const file = new File([blob], "selfie.jpg", { type: "image/jpeg" });

      // Show persistent toast
      const toastId = toast.loading("Uploading Selfie, Please Wait");

      try {
        await uploadAndSearch(file);
        toast.update(toastId, {
          render: "Selfie Uploaded",
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });
      } catch (err) {
        toast.update(toastId, {
          render: "Something Went Wrong",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
      }
    }, "image/jpeg");
  };

  useEffect(() => {
    const modalEl = document.getElementById("scanModal");

    const handleShown = () => {
      startCamera();
    };
    const handleHidden = () => {
      stopCamera();
      setCapturedImage(null);
    };

    if (modalEl) {
      modalEl.addEventListener("shown.bs.modal", handleShown);
      modalEl.addEventListener("hidden.bs.modal", handleHidden);
    }

    return () => {
      if (modalEl) {
        modalEl.removeEventListener("shown.bs.modal", handleShown);
        modalEl.removeEventListener("hidden.bs.modal", handleHidden);
      }
    };
  }, [stream]);

  const uploadAndSearch = async (file) => {
    try {
      const fileName = `selfie-${Date.now()}.jpg`;
      const presignedRes = await fetch(
        `${server}/PresignedUrl/GeneratePresignedUrl`,
        {
          method: "POST",
          headers: {
            Authorization: token,
          },
          body: JSON.stringify({
            folder: "search",
            fileNames: [fileName],
          }),
        }
      );

      const presignedData = await presignedRes.json();
      const uploadDetails = presignedData.urls[0];
      const s3Key = uploadDetails.imageKey;

      // Step 2: Upload the file to S3
      await fetch(uploadDetails.signedUrl, {
        method: "PUT",
        headers: {
          "Content-Type": uploadDetails.contentType,
        },
        body: file,
      });

      // Step 3: Call SearchFace API with the correct key
      const searchRes = await fetch(`${server}/Search/SearchFace`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          eventId: event,
          key: s3Key,
        }),
      });

      const searchData = await searchRes.json();

      if (searchData.images && searchData.images.length > 0) {
        const closeButton = document.querySelector(
          "#scanModal .btn-close-modal"
        );
        if (closeButton) closeButton.click();
        setSearchResults(searchData.images);
      } else {
        toast.info("No Matches Found");
      }
    } catch (err) {
      toast.error("Something Went Wrong");
    }
  };

  const toggleSelectMode = () => {
    setIsSelectMode(!isSelectMode);
    if (isSelectMode) setSelectedImages([]);
  };

  const toggleImageSelect = (id) => {
    setSelectedImages((prev) =>
      prev.includes(id) ? prev.filter((imgId) => imgId !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedImages.length === images.length) {
      setSelectedImages([]);
    } else {
      setSelectedImages(images.map((img) => img._id));
    }
  };

  const handleSingleDownload = async (imgUrl, fileName = "image.jpg") => {
    try {
      const response = await fetch(imgUrl, { mode: "cors" });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const handleMultiDownload = async () => {
    if (selectedImages.length === 0) return;

    try {
      const zip = new JSZip();
      const folder = zip.folder("images");
      const selectedFiles = images.filter((img) =>
        selectedImages.includes(img._id)
      );

      for (const [index, img] of selectedFiles.entries()) {
        const response = await fetch(img.imageUrl);
        const blob = await response.blob();
        folder.file(`image_${index + 1}.jpg`, blob);
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, "selected_images.zip");
    } catch (error) {
      console.error("ZIP download failed:", error);
    }
  };

  useEffect(() => {
    const lightbox = GLightbox({
      selector: ".glightbox",
    });

    return () => lightbox.destroy();
  }, [images, searchResults]);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} bytes`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(1)} MB`;
  };

  const handleRemove = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const addFilesWithLimit = (newFiles) => {
    if (files.length + newFiles.length > MAX_FILES) {
      const allowed = MAX_FILES - files.length;

      toast.error(`You can upload only ${MAX_FILES} images.`, {
        position: "bottom-center",
        autoClose: 3000,
      });

      const limitedFiles = newFiles.slice(0, allowed);
      processFiles(limitedFiles);
    } else {
      processFiles(newFiles);
    }
  };

  const processFiles = async (selectedFiles) => {
    setProcessingFiles(true); // START LOADING

    const processed = await Promise.all(
      selectedFiles.map((file) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            resolve({
              file,
              preview: URL.createObjectURL(file),
              status: "pending",
              width: img.width,
              height: img.height,
              size: formatFileSize(file.size),
            });
          };
          img.src = URL.createObjectURL(file);
        });
      })
    );

    setFiles((prev) => [...prev, ...processed]);

    setProcessingFiles(false); // END LOADING
  };

  const handleUpload = async () => {
    try {
      setUploading(true);
      setFiles((prev) => prev.map((f) => ({ ...f, status: "uploading" })));

      const uploadBatchSize = 5;
      const totalFiles = files.length;
      let keysBuffer = [];
      let stopUpload = false; // 🔥 NEW: control flag

      // Helper: Upload key chunk & detect limit error
      const uploadKeyChunk = async (chunk) => {
        const res = await fetch(`${server}/Upload/UploadImage`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({
            userObjectId: eventDetails.userObjectId,
            eventId: eventDetails.eventId,
            keys: chunk,
          }),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));

          if (
            res.status === 403 ||
            err?.error?.toLowerCase().includes("photo limit")
          ) {
            setLimitError("Photo limit reached — Please upgrade your plan");
            stopUpload = true;
            return false;
          }
        }

        return true;
      };

      // ────────────────────────────────────────────
      // MAIN UPLOAD LOOP
      // ────────────────────────────────────────────
      for (
        let start = 0;
        start < totalFiles && !stopUpload;
        start += uploadBatchSize
      ) {
        const currentBatch = files.slice(start, start + uploadBatchSize);
        currentBatchRef.current = start / uploadBatchSize;

        // 1️⃣ Compress
        const compressedBatch = await Promise.all(
          currentBatch.map(async (f) => {
            const options = {
              maxSizeMB: 1,
              maxWidthOrHeight: 1920,
              useWebWorker: true,
            };
            try {
              let blob = await imageCompression(f.file, options);
              if (blob.size > 1024 * 1024) {
                blob = await imageCompression(f.file, {
                  ...options,
                  maxSizeMB: 0.8,
                });
              }
              return new File([blob], f.file.name, { type: f.file.type });
            } catch {
              return f.file;
            }
          })
        );

        // 2️⃣ Presigned URLs
        const presignedRes = await fetch(
          `${server}/PresignedUrl/GeneratePresignedUrl`,
          {
            method: "POST",
            headers: { Authorization: token },
            body: JSON.stringify({
              folder: "event-uploads",
              fileNames: compressedBatch.map((f) => f.name),
            }),
          }
        );

        const presignedData = await presignedRes.json();
        const urls = presignedData.urls;

        // 3️⃣ Upload to S3
        await Promise.all(
          compressedBatch.map((file, i) => {
            const globalIndex = start + i;
            const uploadDetails = urls[i];

            return new Promise((resolve, reject) => {
              const xhr = new XMLHttpRequest();
              xhr.open("PUT", uploadDetails.signedUrl, true);
              xhr.setRequestHeader("Content-Type", uploadDetails.contentType);

              xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                  const percent = Math.round(
                    (event.loaded / event.total) * 100
                  );

                  setUploadProgress((prev) => {
                    const updated = [...prev];
                    updated[globalIndex] = percent;
                    const avg =
                      updated.reduce((sum, v) => sum + (v || 0), 0) /
                      totalFiles;
                    setTotalProgress(Math.round(avg));
                    return updated;
                  });
                }
              };

              xhr.onload = () => {
                if (xhr.status === 200) {
                  setFiles((prev) =>
                    prev.map((f, idx) =>
                      idx === globalIndex ? { ...f, status: "done" } : f
                    )
                  );
                  resolve();
                } else reject(new Error("Upload failed"));
              };

              xhr.onerror = reject;
              xhr.send(file);
            });
          })
        );

        // 4️⃣ Collect keys
        const batchKeys = urls.map((u) => u.imageKey);
        keysBuffer.push(...batchKeys);

        // 5️⃣ Send keys in chunks of 100
        while (keysBuffer.length >= 100 && !stopUpload) {
          const chunk = keysBuffer.slice(0, 100);
          const ok = await uploadKeyChunk(chunk);

          if (!ok) break; // 🔥 Stop the loop if limit reached

          keysBuffer = keysBuffer.slice(100);
        }
      }

      // 6️⃣ Final leftover keys (if not stopped)
      if (!stopUpload && keysBuffer.length > 0) {
        await uploadKeyChunk(keysBuffer);
      }

      if (stopUpload) {
        // Cleanup UI after breaking upload
        setFiles((prev) => prev.map((f) => ({ ...f, status: "pending" })));
        setUploading(false);
        return;
      }

      // 7️⃣ Refresh Gallery
      setLoading(true);
      await fetchImages();
      setLoading(false);

      document.querySelector("#imageUploadModal .btn-close-modal")?.click();

      // Reset UI
      setFiles([]);
      setUploadProgress([]);
      setTotalProgress(0);
      currentBatchRef.current = null;
    } catch (err) {
      setFiles((prev) => prev.map((f) => ({ ...f, status: "pending" })));
    } finally {
      setUploading(false);
    }
  };

  return (
    <main>
      <div className="container">
        <div className="row g-3 d-flex justify-content-center border-bottom border-gray-800">
          <div className="col-12 pt-3">
            <section className="position-relative z-1">
              <div className="row align-items-center mb-4 mt-1">
                <div className="col-lg-7 col-12">
                  <div className="d-flex justify-content-between justify-content-lg-start mb-3">
                    <Link to="/">
                      <img
                        src="/assets/images/logo.png"
                        width="140"
                        alt="logo"
                      />
                    </Link>
                    <Link to="/dashboard" class="btn btn-white ms-5 px-5 fs-6">
                      My Dashboard
                    </Link>
                  </div>
                  <div className="d-flex justify-content-between align-items-center gap-3">
                    <div className="d-flex">
                      <div>
                        <div className="d-flex align-items-center">
                          <Link
                            type="button"
                            data-bs-toggle="modal"
                            data-bs-target="#scanModal"
                            className="profile-image rounded-circle"
                          >
                            <i className="bi bi-person-bounding-box h1 m-0 text-light opacity-75"></i>
                          </Link>
                          <p className="text-white opacity-75 fs-7 m-0 lh-sm ms-3">
                            Upload a clear photo — avoid
                            <br />
                            blurry images.
                          </p>
                        </div>
                        <h4 className="text-white lh-1 mt-4 mb-0">
                          Find Your Photos Instantly
                        </h4>
                        <p className="text-white opacity-75 mb-0 lh-1">
                          Upload a selfie to instantly see every event photo
                          you’re in—no tags, no search.
                        </p>

                        <div className="d-flex mt-3">
                          {!isSelectMode && (
                            <div
                              className="px-6 py-2 bg-light rounded-pill cursor-pointer"
                              onClick={toggleSelectMode}
                            >
                              <p className="m-0 cursor-pointer fs-btn text-dark">
                                <i className="bi bi-check2-circle me-1 text-dark"></i>
                                MutiSelect
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-5 col-12 mt-lg-0 mt-4">
                  <div className="d-flex flex-column justify-content-center">
                    <img
                      src={eventDetails?.eventPhoto}
                      className="w-100 rounded-4 object-fit-cover mb-4"
                      height="130"
                      alt="banner"
                    />
                    <div>
                      <h4 className="mb-0 fs-4 text-light fw-medium lh-1 d-flex">
                        {eventDetails?.name}
                        <span onClick={handleShare}>
                          <i className="bi bi-share text-white ms-3"></i>
                        </span>
                      </h4>
                      <p className="fs-5 me-1 m-0 lh-sm mt-lg-0 mt-2">
                        {eventDetails?.description}
                      </p>
                      <div>
                        <span className="fs-6 text-light">
                          {totalImages} {totalImages === 1 ? "Photo" : "Photos"}
                        </span>
                      </div>
                      <div>
                        {canUpload && (
                          <span
                            type="button"
                            data-bs-toggle="modal"
                            data-bs-target="#imageUploadModal"
                            className="btn btn-dark-success border-0 px-5 rounded-pill fs-6 fw-medium mt-3"
                          >
                            <i className="bi bi-upload me-1"></i> Upload
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-12"></div>
              </div>
            </section>
          </div>
        </div>
      </div>

      {showPinModal && (
        <div className="container d-flex justify-content-center">
          <div className="col-lg-5 col-md-8 col-12 p-5 my-3">
            <div className="text-center">
              <h3 className="fw-semibold text-white lh-1">Verify Event PIN</h3>
              <p className="text-secondary mb-4 lh-1">
                Enter Event PIN sent by creator to unlock this event.
              </p>

              <form onSubmit={handleSubmit} className="needs-validation mt-4">
                <div className="d-flex align-items-center justify-content-center gap-2">
                  {pinDigits.map((digit, index) => (
                    <input
                      key={index}
                      id={`pin-${index}`}
                      type="text"
                      value={digit}
                      onChange={(e) => handleChange(e, index)}
                      className="form-control fs-4 text-white otp-box bg-transparent light-border rounded-3 text-center"
                      maxLength="1"
                    />
                  ))}
                </div>
                <button
                  type="submit"
                  disabled={loading || pinDigits.some((digit) => digit === "")}
                  className="btn btn-gradient-bg-2 border-0 text-white rounded-3 px-6 mt-4 btn-md w-75"
                >
                  {loading ? (
                    <div
                      className="spinner-border spinner-border-sm"
                      role="status"
                    ></div>
                  ) : (
                    "Continue"
                  )}
                </button>
              </form>

              <div className="mt-3 text-center">
                <a href="#" className="fs-5 lh-1">
                  Need Help ? Contact Us
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {searchResults.length > 0 && (
        <div className="container my-4">
          <h5 className="text-light mb-3">
            Search Results ({searchResults.length})
          </h5>

          <div className="row g-2">
            {searchResults.map((imgUrl, idx) => (
              <a href={imgUrl} className="glightbox" key={idx}>
                <img
                  src={imgUrl}
                  alt={`Result ${idx + 1}`}
                  className="w-100 object-fit-cover rounded-4 bg-dark-1"
                  style={{ display: "block" }}
                  draggable={false}
                  loading="lazy"
                />
              </a>
            ))}
          </div>
        </div>
      )}

      {!showPinModal && (
        <div className="container my-4">
          {imagetype === "all" ? (
            <>
              <h5 className="text-light mb-3">
                All Images ({totalImages}{" "}
                {totalImages === 1 ? "Photo" : "Photos"})
              </h5>
              <div className="mt-3 mb-5">
                {images.length > 0 ? (
                  <div className="row g-2">
                    {images.map((img, index) => (
                      <div
                        key={img._id || index}
                        className="position-relative col-lg-2 col-md-4 col-6"
                      >
                        {loadedImages[img._id] && (
                          <div className="position-absolute top-0 end-0 d-flex flex-column m-2 gap-1 z-2">
                            <button
                              className="btn btn-white rounded-circle delete-button-lg p-0"
                              onClick={() =>
                                handleSingleDownload(
                                  img.imageUrl,
                                  `event_image_${index + 1}.jpg`
                                )
                              }
                            >
                              <i className="bi bi-download sm-icon text-black"></i>
                            </button>
                          </div>
                        )}

                        <a href={img.imageUrl} className="glightbox">
                          <img
                            src={`${img.thumbnailUrl}`}
                            data-full={img.thumbnailUrl}
                            className="w-100 rounded-4 object-fit-cover"
                            height="170"
                            loading="lazy"
                            draggable={false}
                            style={{
                              opacity: loadedImages[img._id] ? 1 : 0,
                              transition: "opacity 0.3s ease",
                            }}
                            onLoad={(e) => {
                              const full = new Image();
                              full.src = e.target.dataset.full;
                              full.onload = () => {
                                e.target.src = full.src;
                                setLoadedImages((prev) => ({
                                  ...prev,
                                  [img._id]: true,
                                }));
                              };
                            }}
                            alt="image"
                          />
                        </a>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="d-flex justify-content-center mt-5">
                    <div
                      className="spinner-border text-secondary"
                      role="status"
                    ></div>
                  </div>
                )}

                {!loading && totalPages > 1 && (
                  <div className="d-flex justify-content-center fixed-bottom">
                    <nav>
                      <ul className="pagination">
                        <li
                          className={`page-item ${
                            page === 1 ? "disabled" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => setPage(page - 1)}
                          >
                            <i className="bi bi-chevron-left"></i>
                          </button>
                        </li>

                        {[...Array(totalPages)]
                          .slice(
                            Math.max(0, page - 3),
                            Math.min(totalPages, page + 2)
                          )
                          .map((_, i) => {
                            const pageNumber = Math.max(1, page - 2) + i;
                            return (
                              <li
                                key={pageNumber}
                                className={`page-item ${
                                  page === pageNumber ? "active" : ""
                                }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => setPage(pageNumber)}
                                >
                                  {pageNumber}
                                </button>
                              </li>
                            );
                          })}

                        <li
                          className={`page-item ${
                            page === totalPages ? "disabled" : ""
                          }`}
                        >
                          <button
                            className="page-link me-0"
                            onClick={() => setPage(page + 1)}
                          >
                            <i className="bi bi-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-3">
              <span
                type="button"
                data-bs-toggle="modal"
                data-bs-target="#scanModal"
                className="btn btn-dark-success border-0 px-5 rounded-pill fs-6 fw-medium"
              >
                <i className="bi bi-upload me-1"></i> Upload
              </span>
              <p className="fs-8 lh-sm m-0 fw-light text-center mt-3">
                Upload your selfie to find your images
              </p>
            </div>
          )}
        </div>
      )}

      {isSelectMode && (
        <div className="fixed-bottom container w-lg-50 w-100 mt-7 mb-3 d-flex">
          <div className="bg-white py-2 ps-5 rounded-4 col-12 overflow-hidden">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <span
                  onClick={toggleSelectMode}
                  className="fs-4 rounded-3 me-3 cursor-pointer"
                >
                  <i className="bi bi-x-lg"></i>
                </span>
                <h5 className="m-0 fw-normal">
                  {isSelectMode
                    ? `Selected (${selectedImages.length}/${images.length})`
                    : "Select Images"}
                </h5>
              </div>
              <div className="d-flex align-items-center">
                <span
                  className={`fs-6 rounded-3 me-1 circle-md-btn rounded-circle cursor-pointer btn btn-secondary ${
                    selectedImages.length === 0 ? "" : ""
                  }`}
                  onClick={
                    selectedImages.length > 0 ? handleMultiDownload : undefined
                  }
                >
                  <i className="bi bi-download text-white"></i>
                </span>
                <span
                  onClick={toggleSelectAll}
                  className="btn btn-primary ps-4 pe-7 fs-6 rounded-start-pill"
                >
                  {selectedImages.length === images.length
                    ? "De-Select"
                    : "Select"}{" "}
                  All
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <footer className="footer">
        <div className="container">
          <div className="row align-items-center g-0 border-top border-gray-800 py-5 flex-column gap-1 flex-lg-row gap-lg-0">
            <div className="col-lg-6 col-12 text-center text-md-start lh-sm">
              <img
                src="/assets/images/logo.png"
                width="140"
                className="mb-2"
                alt="logo"
              />
              <br />
              <span>Visualez Mediaa Works Private Limited</span>
              <br />
              <span>CIN : U98200MH2024PTC425556</span>
            </div>
            <div className="col-lg-6 col-12">
              <p className="fs-8 lh-sm m-0 fw-light text-center text-lg-end">
                <u>Visualaey</u> is a next-generation, AI-powered{" "}
                <u>event photo-sharing platform</u> that transforms how memories
                are discovered and shared. Built on secure, cloud-native
                infrastructure using facial recognition to match guests
                seamlessly.
              </p>
            </div>
          </div>
          <div className="row align-items-center g-0 border-top border-gray-800 py-3 flex-column gap-1 flex-lg-row gap-lg-0">
            <div className="col-lg-6 col-12 text-center text-md-start">
              <span>Copyright © 2025 Visualaey®</span>
            </div>
            <div className="col-12 col-lg-6">
              <nav className="nav nav-footer justify-content-center justify-content-md-start justify-content-lg-end">
                <a className="nav-link active" href="#!">
                  Terms of Use
                </a>
                <a className="nav-link" href="#!">
                  Cookies Settings
                </a>
                <a className="nav-link" href="#!">
                  Privacy Policy
                </a>
              </nav>
            </div>
          </div>
        </div>
      </footer>

      <div
        className="modal fade"
        id="scanModal"
        tabIndex="-1"
        aria-labelledby="scanModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content rounded-4">
            <div className="modal-body pt-3 pb-2">
              <h4 className="mb-0 mt-3 normal-text lh-1 text-center">
                <span className="text-dark ls-sm fs-4">
                  Find Your Photos Instantly
                </span>
              </h4>
              <p className="m-0 fs-5 ls-sm text-center text-dark opacity-75 lh-1 mt-2">
                Upload a selfie to instantly see every event photo
                <br />
                you’re in—no tags, no search.
              </p>

              <div className="d-flex flex-column align-items-center justify-content-center mt-4">
                {!capturedImage ? (
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    width="180"
                    height="180"
                    className="object-fit-cover rounded-circle bg-dark"
                  />
                ) : (
                  <img
                    src={capturedImage}
                    width="180"
                    height="180"
                    className="object-fit-cover rounded-circle"
                    alt="Captured Selfie"
                  />
                )}

                <canvas ref={canvasRef} style={{ display: "none" }} />

                <button
                  className="btn btn-dark-primary border-0 px-5 rounded-3 fs-6 fw-medium mt-4"
                  onClick={capturePhoto}
                  disabled={!stream}
                >
                  <i className="bi bi-camera me-1"></i> Upload Selfie
                </button>
              </div>
            </div>
            <div className="modal-footer border-0 p-0">
              <button
                type="button"
                className="btn btn-success rounded-circle px-3 label-text text-white btn-close-modal me-n3 mb-n2"
                data-bs-dismiss="modal"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {canUpload && (
        <div
          className="modal fade"
          id="imageUploadModal"
          tabIndex="-1"
          aria-labelledby="imageUploadModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content rounded-4">
              <div className="modal-body">
                <div className="position-relative">
                  <button
                    type="button"
                    className="btn btn-success rounded-circle px-3 label-text text-white btn-close-modal me-n3 mb-n2 position-absolute"
                    data-bs-dismiss="modal"
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
                <div className="row py-1">
                  <div className="col-lg-6 col-12 d-flex justify-content-center align-items-center">
                    <div className="ms-4 me-2">
                      <h4 className="mb-0 normal-text lh-1 text-center">
                        <span className="text-dark ls-sm fs-4">
                          Upload Photos — Quick & Easy
                        </span>
                      </h4>
                      <p className="m-0 fs-5 ls-sm text-center text-dark opacity-75 lh-1 mt-2">
                        Upload photos to instantly add them to your event
                        <br />
                        gallery — fast, easy, and organized.
                      </p>

                      <div
                        id="my-dropzone"
                        className="dropzone mt-3 border-dashed rounded-4 min-h-0 text-center py-6 px-8 text-muted ls-sm cursor-pointer flex-column"
                        onClick={() =>
                          document.getElementById("hiddenFileInput").click()
                        }
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const droppedFiles = Array.from(e.dataTransfer.files);
                          addFilesWithLimit(droppedFiles);
                        }}
                      >
                        Drag & drop files here, or{" "}
                        <span className="text-danger px-1">click here</span> to
                        select
                        <small className="d-block text-muted">
                          or choose a folder
                        </small>
                      </div>
                      <div className="d-flex justify-content-center pt-1">
                        <button
                          className="bg-dark text-white mt-2 rounded-pill border-0 px-4 py-2 fs-6"
                          type="button"
                          onClick={() =>
                            document.getElementById("folderFileInput").click()
                          }
                        >
                          <i className="bi bi-folder me-2"></i>Upload from
                          Folder
                        </button>
                      </div>

                      <input
                        type="file"
                        id="hiddenFileInput"
                        multiple
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const selectedFiles = Array.from(e.target.files);
                          addFilesWithLimit(selectedFiles);
                        }}
                      />

                      <input
                        type="file"
                        id="folderFileInput"
                        webkitdirectory="true"
                        directory=""
                        multiple
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const folderFiles = Array.from(e.target.files).filter(
                            (f) => f.type.startsWith("image/")
                          );
                          addFilesWithLimit(folderFiles);
                        }}
                      />

                      <div className="d-flex flex-column justify-content-center mt-3">
                        {uploading && (
                          <div className="w-100 p-3 rounded-4 bg-danger-soft mb-1">
                            <div className="progress rounded-pill progress-bar-height">
                              <div
                                className="progress-bar progress-bar-striped bg-success"
                                role="progressbar"
                                style={{ width: `${totalProgress}%` }}
                                aria-valuenow={totalProgress}
                                aria-valuemin="0"
                                aria-valuemax="100"
                              ></div>
                            </div>
                            <p className="m-0 text-center text-dark lh-1 mt-2">
                              {files.filter((f) => f.status === "done").length}/
                              {files.length} Photos Uploaded ({totalProgress}%)
                            </p>
                          </div>
                        )}

                        <button
                          className="btn btn-dark-primary border-0 px-4 rounded-pill fs-6 fw-medium"
                          onClick={handleUpload}
                          disabled={uploading || files.length === 0}
                        >
                          {uploading ? (
                            <div
                              className="spinner-border spinner-border-sm"
                              role="status"
                            ></div>
                          ) : (
                            <>
                              <i className="bi bi-upload me-1"></i> Upload Now
                            </>
                          )}
                        </button>
                        {uploading && (
                          <div className="text-danger rounded-4 py-2 px-3 text-center">
                            <span className="fst-italic">
                              ⚠️ Upload in progress — don’t close this tab.
                            </span>
                          </div>
                        )}

                        {limitError && (
                          <div className="alert alert-danger mt-2 rounded-4 text-center">
                            {limitError}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-6 col-12 d-flex justify-content-center align-items-center">
                    <div className="w-100">
                      <div
                        className="col-12 h-upload-images px-4 ps-lg-0"
                        ref={scrollContainerRef}
                      >
                        {processingFiles ? (
                          <div className="d-flex flex-column justify-content-center align-items-center h-100">
                            <div
                              className="spinner-border text-dark mb-2"
                              role="status"
                            ></div>
                          </div>
                        ) : files.length > 0 ? (
                          files.map((f, i) => (
                            <div
                              key={i}
                              data-batch={Math.floor(i / 5)}
                              className={`col-12 pt-2 rounded-4 ${
                                f.status === "done"
                                  ? "bg-success-soft"
                                  : Math.floor(i / 5) ===
                                    currentBatchRef.current
                                  ? "bg-primary-soft"
                                  : "bg-light"
                              }`}
                            >
                              <div className="d-flex justify-content-between px-5">
                                <p className="pe-8 text-truncate lh-sm m-0">
                                  <span className="text-dark">
                                    {f.file.name}
                                  </span>
                                  <br />
                                  {(f.width && f.height) || f.size ? (
                                    <small className="text-secondary">
                                      {f.width &&
                                        f.height &&
                                        `${f.width}×${f.height}px`}
                                      {f.width && f.height && f.size
                                        ? " • "
                                        : ""}
                                      {f.size && f.size}
                                    </small>
                                  ) : null}
                                </p>
                                <div className="d-flex align-items-center">
                                  <button
                                    disabled={f.status !== "pending"}
                                    className="btn btn-dark rounded-circle delete-button position-relative z-2 p-0"
                                    onClick={() => handleRemove(i)}
                                  >
                                    <i className="bi bi-x-lg sm-icon text-white"></i>
                                  </button>
                                </div>
                              </div>

                              <div className="mx-3 pt-1">
                                <div
                                  className="progress progress-bar-height my-1 w-95"
                                  role="progressbar"
                                  aria-valuemin="0"
                                  aria-valuemax="100"
                                >
                                  <div
                                    className={`progress-bar progress-bar-striped ${
                                      f.status === "done"
                                        ? "bg-success"
                                        : "bg-primary"
                                    }`}
                                    style={{
                                      width: `${uploadProgress[i] || 0}%`,
                                      transition: "width 0.3s ease",
                                    }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="d-flex justify-content-center align-items-center h-100">
                            <img
                              src="/assets/images/images.png"
                              className="w-50"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default Shared;
